import { c as _c } from "react/compiler-runtime";
import { type RechartsXAxisTickContentProps as TickContentProps, type RechartsTickProp as TickProp } from '@precis-digital/kurama/recharts';
import { TooltipBody } from '@precis-digital/kurama/Tooltip';
import { type ReactElement, type ReactNode } from 'react';
import { getAlertSVGInfoForReact } from '../../Alert/AlertSymbol';
import AlertTooltip from '../../Alert/AlertTooltip';
import type { ElementDescription, FontProps } from '../internalTypes';
import type { ChartCardDefaults, ChartCardTFunction } from '../types';
import { truncateTextForAngledLabels } from '../utils/axis';
import { getCanvasContext } from '../utils/graph';
import { defaultTFunction, getDimensionLabel, isNullDimensionValue } from '../utils/generics';
const renderMultilineText = (text: string, x: string | number): ReactNode => {
  if (!text.includes('\n')) return text;
  return text.split('\n').map((line, i) => <tspan key={`${i}-${line}`} x={x} dy={i === 0 ? 0 : '1em'}>
      {line}
    </tspan>);
};
const getContext = (font?: string | FontProps): CanvasRenderingContext2D | null => getCanvasContext(font).context;
export const createCustomXAxisTick = (axisDescription: ElementDescription, defaults: ChartCardDefaults, t: ChartCardTFunction = defaultTFunction, visibleLabels?: boolean[], dataKey?: string): TickProp<TickContentProps> => {
  const {
    label,
    descriptions
  } = axisDescription;
  const context = getContext(defaults.fonts.axisTicks);
  const CustomXAxisTick = tickProps => {
    const $ = _c(44);
    const {
      index,
      x,
      y,
      stroke,
      orientation,
      payload
    } = tickProps;
    const textAnchor = orientation === "bottom" === defaults.axis.angledLabels.angle < 0 ? "end" : "start";
    const visibleLabel = visibleLabels == null || visibleLabels[index];
    if (!visibleLabel) {
      let t0;
      if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
        t0 = <></>;
        $[0] = t0;
      } else {
        t0 = $[0];
      }
      return t0;
    }
    let t0;
    if ($[1] !== payload.value) {
      t0 = isNullDimensionValue(payload.value);
      $[1] = payload.value;
      $[2] = t0;
    } else {
      t0 = $[2];
    }
    const isNullValue = t0;
    let alert;
    let alertSize;
    let alertText;
    let fullText;
    let svgInfo;
    let t1;
    if ($[3] !== payload.value || $[4] !== x) {
      fullText = getDimensionLabel(axisDescription, payload.value, dataKey);
      let t2;
      if ($[11] !== payload.value) {
        ({
          alert,
          alertText
        } = descriptions?.[payload.value] ?? {});
        alertSize = 0;
        if (alert != null) {
          if (context == null) {
            alertSize = 12;
          } else {
            let t3;
            if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
              t3 = context.measureText(" ");
              $[16] = t3;
            } else {
              t3 = $[16];
            }
            const {
              fontBoundingBoxAscent,
              fontBoundingBoxDescent
            } = t3;
            alertSize = fontBoundingBoxAscent + fontBoundingBoxDescent;
          }
        }
        t2 = getAlertSVGInfoForReact(alert, "chart-card-icon-templates");
        $[11] = payload.value;
        $[12] = alert;
        $[13] = alertSize;
        $[14] = alertText;
        $[15] = t2;
      } else {
        alert = $[12];
        alertSize = $[13];
        alertText = $[14];
        t2 = $[15];
      }
      svgInfo = t2;
      const text = truncateTextForAngledLabels({
        text: fullText,
        textOffset: -alertSize * 1.5,
        maxSpace: defaults.axis.angledLabels.maxSpace,
        wrapLines: defaults.axis.angledLabels.wrapLines,
        angle: defaults.axis.angledLabels.angle,
        context
      });
      t1 = renderMultilineText(text, x);
      $[3] = payload.value;
      $[4] = x;
      $[5] = alert;
      $[6] = alertSize;
      $[7] = alertText;
      $[8] = fullText;
      $[9] = svgInfo;
      $[10] = t1;
    } else {
      alert = $[5];
      alertSize = $[6];
      alertText = $[7];
      fullText = $[8];
      svgInfo = $[9];
      t1 = $[10];
    }
    const textElements = t1;
    const t2 = `rotate(${defaults.axis.angledLabels.angle}, ${x}, ${y})`;
    const t3 = -alertSize * 1.5;
    let t4;
    if ($[17] !== stroke || $[18] !== t3 || $[19] !== textAnchor || $[20] !== textElements || $[21] !== x || $[22] !== y) {
      t4 = <text fontWeight={defaults.fonts.axisTicks.fontWeight} fontSize={defaults.fonts.axisTicks.fontSize} fontFamily={defaults.fonts.axisTicks.fontFamily} letterSpacing={defaults.fonts.axisTicks.letterSpacing} fill={defaults.fonts.axisTicks.fill} x={x} y={y} dy="1em" dx={t3} textAnchor={textAnchor} stroke={stroke}>{textElements}</text>;
      $[17] = stroke;
      $[18] = t3;
      $[19] = textAnchor;
      $[20] = textElements;
      $[21] = x;
      $[22] = y;
      $[23] = t4;
    } else {
      t4 = $[23];
    }
    let t5;
    if ($[24] !== alertSize || $[25] !== svgInfo || $[26] !== x || $[27] !== y) {
      t5 = svgInfo != null && <g transform={`translate(${x}, ${y})`}><svg x={-alertSize} y={0} width={defaults.fonts.axisTicks.fontSize} height={defaults.fonts.axisTicks.fontSize} viewBox={svgInfo.viewBox}>{svgInfo.children}</svg></g>;
      $[24] = alertSize;
      $[25] = svgInfo;
      $[26] = x;
      $[27] = y;
      $[28] = t5;
    } else {
      t5 = $[28];
    }
    let t6;
    if ($[29] !== t2 || $[30] !== t4 || $[31] !== t5) {
      t6 = <g transform={t2}>{t4}{t5}</g>;
      $[29] = t2;
      $[30] = t4;
      $[31] = t5;
      $[32] = t6;
    } else {
      t6 = $[32];
    }
    const tickElement = t6;
    const t7 = label ?? (isNullValue ? fullText : payload.value);
    let t8;
    if ($[33] !== fullText || $[34] !== isNullValue) {
      t8 = isNullValue ? t("noData") : fullText;
      $[33] = fullText;
      $[34] = isNullValue;
      $[35] = t8;
    } else {
      t8 = $[35];
    }
    let t9;
    if ($[36] !== t8) {
      t9 = <TooltipBody>{t8}</TooltipBody>;
      $[36] = t8;
      $[37] = t9;
    } else {
      t9 = $[37];
    }
    let t10;
    if ($[38] !== alert || $[39] !== alertText || $[40] !== t7 || $[41] !== t9 || $[42] !== tickElement) {
      t10 = <AlertTooltip element={tickElement} alert={alert} alertText={alertText} description={t7} descriptionBody={t9} />;
      $[38] = alert;
      $[39] = alertText;
      $[40] = t7;
      $[41] = t9;
      $[42] = tickElement;
      $[43] = t10;
    } else {
      t10 = $[43];
    }
    return t10;
  };
  return CustomXAxisTick;
};